import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة البي بال - احسب رسوم وعمولة PayPal بدقة وسرعة";
const DESCRIPTION =
  "حاسبة البي بال المجانية: احسب رسوم وعمولة PayPal عند الإرسال والاستقبال، واعرف المبلغ الصافي أو الإجمالي بعد الخصم. تدعم جميع العملات وأنواع المعاملات.";
const KEYWORDS =
  "حاسبة البي بال, رسوم وعمولة PayPal, حساب عمولة باي بال, رسوم بايبال, PayPal fee calculator, حاسبة رسوم وعمولة PayPal, كم رسوم باي بال";
const OG_URL = "https://do-calculate.com/calculator/ar/paypal-fee/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة البي بال",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة البي بال",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "كم رسوم PayPal على المعاملات التجارية؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "الرسوم القياسية للمعاملات التجارية عبر PayPal هي 3.49% + رسوم ثابتة حسب العملة. للمعاملات بالدولار الأمريكي الرسوم الثابتة 0.49 دولار. تختلف النسبة والرسوم الثابتة حسب نوع المعاملة والعملة المستخدمة.",
        },
      },
      {
        "@type": "Question",
        name: "هل رسوم PayPal مجانية بين الأصدقاء والعائلة؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "داخل نفس الدولة، إرسال الأموال عبر خيار الأصدقاء والعائلة (Personal) مجاني إذا كانت المصدر حساب باي بال أو حساب بنكي. أما إذا استخدمت بطاقة ائتمان، فتُضاف رسوم 2.9%. للتحويلات الدولية تُطبق رسوم تحويل العملة.",
        },
      },
      {
        "@type": "Question",
        name: "كيف أحسب المبلغ الذي يجب أن أرسله لكي يصل مبلغ معين بعد الرسوم؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "إذا أردت أن يصل المستقبل 100 دولار بعد خصم رسوم PayPal التجارية (3.49% + 0.49 دولار)، فعليك إرسال حوالي 104.12 دولار. استخدم حاسبتنا واختر 'أريد أن يستلم المبلغ كاملاً' وأدخل 100 دولار لتحصل على المبلغ الذي يجب إرساله.",
        },
      },
    ],
  },
];

// Fixed fees in USD — will be converted dynamically via live exchange rates
// Source: PayPal fee schedule (USD base values)
const FIXED_FEES_USD = {
  standard:    { rate: 0.0349, fixedUSD: 0.49 },
  checkout:    { rate: 0.0299, fixedUSD: 0.49 },
  micropayment:{ rate: 0.05,   fixedUSD: 0.05 },
  friends:     { rate: 0.0,    fixedUSD: 0.00 },
  international:{ rate: 0.045, fixedUSD: 0.49 },
};

const TRANSACTION_TYPES = {
  standard:     { name: "معاملة تجارية قياسية (Standard)" },
  checkout:     { name: "باي بال تشيك-آوت (Checkout)" },
  micropayment: { name: "مدفوعات صغيرة (Micropayment)" },
  friends:      { name: "أصدقاء وعائلة (Friends & Family)" },
  international:{ name: "تحويل دولي (International)" },
};

// All currencies: Arabic-world countries first, then major global
const CURRENCIES = {
  // ── الدول العربية ──────────────────────────────
  SAR: { name: "ريال سعودي — المملكة العربية السعودية (SAR)", symbol: "ر.س" },
  AED: { name: "درهم إماراتي — الإمارات (AED)", symbol: "د.إ" },
  KWD: { name: "دينار كويتي — الكويت (KWD)", symbol: "د.ك" },
  BHD: { name: "دينار بحريني — البحرين (BHD)", symbol: "د.ب" },
  OMR: { name: "ريال عُماني — عُمان (OMR)", symbol: "ر.ع" },
  QAR: { name: "ريال قطري — قطر (QAR)", symbol: "ر.ق" },
  JOD: { name: "دينار أردني — الأردن (JOD)", symbol: "د.أ" },
  EGP: { name: "جنيه مصري — مصر (EGP)", symbol: "ج.م" },
  IQD: { name: "دينار عراقي — العراق (IQD)", symbol: "د.ع" },
  LYD: { name: "دينار ليبي — ليبيا (LYD)", symbol: "د.ل" },
  TND: { name: "دينار تونسي — تونس (TND)", symbol: "د.ت" },
  DZD: { name: "دينار جزائري — الجزائر (DZD)", symbol: "د.ج" },
  MAD: { name: "درهم مغربي — المغرب (MAD)", symbol: "د.م" },
  SDG: { name: "جنيه سوداني — السودان (SDG)", symbol: "ج.س" },
  SYP: { name: "ليرة سورية — سوريا (SYP)", symbol: "ل.س" },
  LBP: { name: "ليرة لبنانية — لبنان (LBP)", symbol: "ل.ل" },
  YER: { name: "ريال يمني — اليمن (YER)", symbol: "ر.ي" },
  // ── عملات عالمية رئيسية ──────────────────────
  USD: { name: "دولار أمريكي (USD)", symbol: "$" },
  EUR: { name: "يورو (EUR)", symbol: "€" },
  GBP: { name: "جنيه إسترليني (GBP)", symbol: "£" },
  CAD: { name: "دولار كندي (CAD)", symbol: "C$" },
  AUD: { name: "دولار أسترالي (AUD)", symbol: "A$" },
  JPY: { name: "ين ياباني (JPY)", symbol: "¥" },
  CNY: { name: "يوان صيني (CNY)", symbol: "¥" },
  INR: { name: "روبية هندية (INR)", symbol: "₹" },
  TRY: { name: "ليرة تركية (TRY)", symbol: "₺" },
};

const CALC_MODE = {
  sender: "أريد أن أرسل مبلغاً (كم يصل للمستقبل؟)",
  receiver: "أريد أن يصل مبلغ بعينه (كم أرسل؟)",
};

function PaypalCalculator() {
  const [amount, setAmount] = useState("");
  const [transactionType, setTransactionType] = useState("standard");
  const [currency, setCurrency] = useState("SAR");
  const [calcMode, setCalcMode] = useState("sender");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  // exchangeRates: always keyed from USD base { SAR: 3.75, AED: 3.67, ... }
  const [exchangeRates, setExchangeRates] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState(false);

  // Fetch rates using USD as base — cross-rate formula converts to any currency
  const fetchRates = () => {
    setRatesLoading(true);
    setRatesError(false);
    fetch("https://v6.exchangerate-api.com/v6/c215274af0ae2a4e2f63743a/latest/USD")
      .then((res) => res.json())
      .then((data) => {
        if (data && data.conversion_rates) {
          setExchangeRates(data.conversion_rates);
        } else {
          setRatesError(true);
        }
        setRatesLoading(false);
      })
      .catch(() => {
        setRatesLoading(false);
        setRatesError(true);
      });
  };

  useEffect(() => { fetchRates(); }, []);

  const calculateResult = () => {
    try {
      setError("");
      const amountNum = parseFloat(amount);
      if (!amountNum || amountNum <= 0) {
        setResult(null);
        return;
      }

      const txConfig = FIXED_FEES_USD[transactionType];
      const rate = txConfig.rate;

      // Convert the USD fixed fee to the selected currency using live cross-rates
      // Cross-rate: fixedInCurrency = fixedUSD * rates[currency]
      let fixedFee = txConfig.fixedUSD;
      let usdRate = 1;
      if (exchangeRates && currency !== "USD") {
        usdRate = exchangeRates[currency] || 1;
        fixedFee = txConfig.fixedUSD * usdRate;
      }

      let feePaid, amountSent, amountReceived;

      if (calcMode === "sender") {
        amountSent = amountNum;
        feePaid = amountSent * rate + fixedFee;
        amountReceived = amountSent - feePaid;
      } else {
        // sent = (received + fixed) / (1 - rate)
        amountReceived = amountNum;
        amountSent = (amountReceived + fixedFee) / (1 - rate);
        feePaid = amountSent - amountReceived;
      }

      if (amountReceived < 0) {
        setError("المبلغ أقل من الرسوم الثابتة! يرجى إدخال مبلغ أكبر.");
        setResult(null);
        return;
      }

      const feePercentage = (feePaid / amountSent) * 100;
      // Always compute USD equivalent of the fee
      const feeInUSD = currency === "USD" ? feePaid : feePaid / usdRate;

      setResult({
        amountSent,
        amountReceived,
        feePaid,
        feePercentage,
        fixedFee,
        fixedFeeUSD: txConfig.fixedUSD,
        feeInUSD,
        ratePercent: rate * 100,
        transactionName: TRANSACTION_TYPES[transactionType].name,
        currencySymbol: CURRENCIES[currency].symbol,
        currencyCode: currency,
        usdRate,
        mode: calcMode,
      });
    } catch (err) {
      setError("خطأ في الحساب. يرجى مراجعة البيانات المدخلة.");
      setResult(null);
    }
  };

  useEffect(() => {
    if (amount) {
      calculateResult();
    }
  }, [amount, transactionType, currency, calcMode]);

  const handleClear = () => {
    setAmount("");
    setTransactionType("standard");
    setCurrency("SAR");
    setCalcMode("sender");
    setResult(null);
    setError("");
  };

  const fmt = (num, decimals = 2) =>
    num !== undefined && num !== null ? num.toFixed(decimals) : "—";

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة البي بال</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            {/* Introduction */}
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>حاسبة البي بال</strong> أداة مجانية ودقيقة تساعدك على{" "}
                <strong>حساب رسوم PayPal</strong> لأي معاملة، سواء كنت مرسلاً أو مستقبلاً. أدخل المبلغ وحدد نوع المعاملة واحصل فوراً على الرسوم والمبلغ الصافي. مثالية للبائعين، المستقلين، وأصحاب المتاجر الإلكترونية.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            {/* Calculator Section */}
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  احسب رسوم PayPal بسهولة
                </h2>
              </div>

              {/* Calc Mode */}
              <div className="row">
                <div className="col-sm-12">
                  <label htmlFor="calcMode" >
                    ماذا تريد أن تحسب؟
                  </label>
                  <select
                    id="calcMode"
                    className="form-select"
                    value={calcMode}
                    onChange={(e) => setCalcMode(e.target.value)}
                  >
                    {Object.entries(CALC_MODE).map(([key, val]) => (
                      <option key={key} value={key}>
                        {val}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Transaction Type */}
              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="transactionType" >
                    نوع المعاملة
                  </label>
                  <select
                    id="transactionType"
                    className="form-select"
                    value={transactionType}
                    onChange={(e) => setTransactionType(e.target.value)}
                  >
                    {Object.entries(TRANSACTION_TYPES).map(([key, { name }]) => (
                      <option key={key} value={key}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="currency" >
                    العملة
                  </label>
                  <select
                    id="currency"
                    className="form-select"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                  >
                    {Object.entries(CURRENCIES).map(([code, { name }]) => (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Amount Input */}
              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="amount" >
                    {calcMode === "sender"
                      ? `المبلغ الذي ستُرسله (${CURRENCIES[currency].symbol})`
                      : `المبلغ الذي يجب أن يصل للمستقبل (${CURRENCIES[currency].symbol})`}
                  </label>
                  <input
                    id="amount"
                    type="number"
                    className="form-control"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Exchange Rate Info */}
              {ratesLoading && (
                <div className="alert alert-info mt-2" style={{ fontSize: "14px" }}>
                  ⏳ جارٍ تحميل أسعار الصرف الحية...
                </div>
              )}
              {ratesError && (
                <div className="alert alert-warning mt-2" style={{ fontSize: "14px" }}>
                  ⚠️ تعذّر تحميل أسعار الصرف.{" "}
                  <span
                    onClick={fetchRates}
                    style={{ cursor: "pointer", textDecoration: "underline", fontWeight: "bold" }}
                  >
                    أعد المحاولة
                  </span>
                </div>
              )}
              {exchangeRates && !ratesLoading && currency !== "USD" && (
                <div className="alert alert-info mt-2" style={{ fontSize: "14px" }}>
                  💱 سعر الصرف الحالي: 1 USD = {(exchangeRates[currency] || 0).toFixed(4)} {currency}
                </div>
              )}

              {/* Buttons */}
              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{
                    fontSize: "16px",
                    padding: "10px 30px",
                  }}
                >
                  امسح
                </button>
              </div>

              {/* Error */}
              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>تنبيه:</strong> {error}
                </div>
              )}

              {/* Results */}
              {result && (
                <div className="mt-4">
                  <h3
                    style={{
                      fontSize: "18px",
                      marginBottom: "15px",
                      textAlign: "center",
                    }}
                  >
                    <strong>النتيجة:</strong>
                  </h3>
                  {/* Main Results Table */}
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            البيان
                          </th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            القيمة
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>المبلغ المُرسَل</td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#1a5f3f",
                            }}
                          >
                            {fmt(result.amountSent)} {result.currencySymbol}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>رسوم PayPal</td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#c0392b",
                            }}
                          >
                            {fmt(result.feePaid)} {result.currencySymbol}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>المبلغ الصافي (يصل للمستقبل)</td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#0f3d28",
                            }}
                          >
                            {fmt(result.amountReceived)} {result.currencySymbol}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>نسبة الرسوم الفعلية</td>
                          <td style={{ textAlign: "center", fontSize: "16px" }}>
                            {fmt(result.feePercentage)}%
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>تفاصيل الرسوم</strong>
                      </div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                          <li>
                            💸 <strong>الرسوم النسبية:</strong>{" "}
                            {result.ratePercent}% × {fmt(result.amountSent)} {result.currencySymbol} = {fmt(result.amountSent * result.ratePercent / 100)} {result.currencySymbol}
                          </li>
                          <li>
                            🔒 <strong>الرسوم الثابتة:</strong>{" "}
                            {fmt(result.fixedFee)} {result.currencySymbol}
                            {result.currencyCode !== "USD" && (
                              <span style={{ color: "#888", fontSize: "13px" }}>
                                {" "}(≈ ${fmt(result.fixedFeeUSD)} بسعر الصرف الحالي)
                              </span>
                            )}
                          </li>
                          <li>
                            💰 <strong>إجمالي الرسوم:</strong>{" "}
                            {fmt(result.feePaid)} {result.currencySymbol}
                          </li>
                          {result.currencyCode !== "USD" && (
                            <li>
                              🌐 <strong>الرسوم بالدولار (تقريباً):</strong>{" "}
                              ${fmt(result.feeInUSD)}
                            </li>
                          )}
                          {result.currencyCode !== "USD" && (
                            <li>
                              📊 <strong>سعر الصرف المستخدم:</strong>{" "}
                              1 USD = {fmt(result.usdRate, 4)} {result.currencyCode}
                            </li>
                          )}
                        </ul>
                        <div
                          className="alert alert-info mt-3"
                          style={{ fontSize: "14px" }}
                        >
                          <strong>💡 نوع المعاملة:</strong> {result.transactionName}
                        </div>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>تفاصيل الحساب</strong>
                      </div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p>
                            <strong>طريقة الحساب:</strong>
                          </p>
                          <p style={{ fontSize: "14px", color: "#444" }}>
                            الرسوم = (المبلغ × {result.ratePercent}%) + {fmt(result.fixedFee)} {result.currencySymbol}
                          </p>
                          <p style={{ fontSize: "14px", color: "#444" }}>
                            = ({fmt(result.amountSent)} × {result.ratePercent}%) + {fmt(result.fixedFee)}
                          </p>
                          <p style={{ fontSize: "14px", color: "#444" }}>
                            = {fmt(result.amountSent * result.ratePercent / 100)} + {fmt(result.fixedFee)} = {fmt(result.feePaid)} {result.currencySymbol}
                          </p>
                          <hr />
                          <p style={{ fontSize: "14px", color: "#666" }}>
                            <strong>المبلغ الصافي:</strong>{" "}
                            {fmt(result.amountSent)} - {fmt(result.feePaid)} = {fmt(result.amountReceived)} {result.currencySymbol}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            {/* Related Links */}
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      {/* Informational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>ما هي حاسبة البي بال؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>حاسبة البي بال</strong> أداة إلكترونية مجانية تحسب رسوم PayPal بدقة لأي مبلغ وأي عملة. سواء كنت بائعاً عبر الإنترنت أو مستقلاً تتلقى مدفوعاتك من الخارج، ستعرف في ثوانٍ كم ستُخصم وكم يصل للطرف الآخر.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  كيفية استخدام الحاسبة:
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر ما إذا كنت ستُرسل مبلغاً أو تريد أن يصل مبلغ محدد</li>
                  <li>حدد نوع المعاملة (تجارية، شخصية، دولية)</li>
                  <li>اختر العملة المناسبة من القائمة</li>
                  <li>أدخل المبلغ وستظهر النتيجة فوراً</li>
                  <li>اطلع على تفاصيل الرسوم النسبية والثابتة في قسم التفاصيل</li>
                </ol>
                <div
                  className="alert alert-info mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>💡 ملاحظة:</strong> الحاسبة تجلب أسعار الصرف الحية تلقائياً لعرض قيمة الرسوم بالدولار الأمريكي عند استخدام عملات أخرى، مما يساعدك على فهم التكلفة الفعلية.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>استخدامات حاسبة البي بال</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  للعمل الحر والمستقلين:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>تسعير الخدمات:</strong> احسب كم يجب أن تطلب ليصل إليك المبلغ كاملاً
                  </li>
                  <li>
                    <strong>إصدار الفواتير:</strong> أضف رسوم PayPal إلى فاتورة العميل بشفافية
                  </li>
                  <li>
                    <strong>مقارنة طرق الدفع:</strong> قارن تكلفة PayPal مع البدائل الأخرى
                  </li>
                  <li>
                    <strong>حساب الدخل الصافي:</strong> اعرف ما ستحصل عليه بعد الرسوم قبل إتمام الصفقة
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  للمتاجر الإلكترونية:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>تحديد هامش الربح:</strong> اطرح رسوم PayPal من حساباتك قبل التسعير
                  </li>
                  <li>
                    <strong>تحليل التكاليف:</strong> احسب رسوم الشهر كله بناءً على مبيعاتك
                  </li>
                  <li>
                    <strong>قرارات استراتيجية:</strong> حدد متى يكون PayPal الأفضل ومتى تلجأ لبديل
                  </li>
                  <li>
                    <strong>التفاوض مع العملاء:</strong> وضّح للعميل تكلفة الدفع عبر PayPal
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  للتحويلات الشخصية:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>إرسال الأموال:</strong> اعرف كم يصل إلى صديقك أو عائلتك في الخارج
                  </li>
                  <li>
                    <strong>تقسيم النفقات:</strong> احسب بالضبط كم يدفع كل شخص مع الرسوم
                  </li>
                  <li>
                    <strong>التحويل الدولي:</strong> قارن رسوم PayPal مع الحوالات البنكية
                  </li>
                  <li>
                    <strong>المشتريات الدولية:</strong> اعرف التكلفة الإجمالية قبل الشراء
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  للشركات والمؤسسات:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>التخطيط المالي:</strong> احسب تكاليف بوابة الدفع سنوياً
                  </li>
                  <li>
                    <strong>تقارير المحاسبة:</strong> وثّق الرسوم بدقة لأغراض محاسبية وضريبية
                  </li>
                  <li>
                    <strong>مقارنة بوابات الدفع:</strong> قرر هل PayPal الأنسب لحجم مبيعاتك
                  </li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>جدول رسوم PayPal حسب نوع المعاملة</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  تختلف رسوم PayPal باختلاف نوع المعاملة. الجدول التالي يوضح الرسوم الرئيسية:
                </p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>
                        نوع المعاملة
                      </th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>
                        النسبة
                      </th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>
                        الرسوم الثابتة (USD)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>معاملة تجارية قياسية</td>
                      <td style={{ textAlign: "center" }}>3.49%</td>
                      <td style={{ textAlign: "center" }}>$0.49</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>باي بال تشيك-آوت</td>
                      <td style={{ textAlign: "center" }}>2.99%</td>
                      <td style={{ textAlign: "center" }}>$0.49</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>مدفوعات صغيرة</td>
                      <td style={{ textAlign: "center" }}>5.00%</td>
                      <td style={{ textAlign: "center" }}>$0.05</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>أصدقاء وعائلة (محلي)</td>
                      <td style={{ textAlign: "center" }}>0%</td>
                      <td style={{ textAlign: "center" }}>$0.00</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>تحويل دولي</td>
                      <td style={{ textAlign: "center" }}>4.50%</td>
                      <td style={{ textAlign: "center" }}>$0.49</td>
                    </tr>
                  </tbody>
                </table>
                <div
                  className="alert alert-success mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>✓ نصيحة:</strong> للمبالغ الصغيرة جداً (أقل من 10 دولارات)، جرّب نوع "المدفوعات الصغيرة" لأن رسومه الثابتة أقل بكثير ($0.05 بدلاً من $0.49).
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>أمثلة عملية على حساب رسوم PayPal</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  مثال 1: مستقل يتقاضى 500 دولار
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الحالة:</strong> مصمم جرافيك يريد أن يتقاضى 500 دولار عن مشروع.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>الحساب (معاملة تجارية قياسية):</strong><br />
                  الرسوم = (500 × 3.49%) + $0.49 = $17.45 + $0.49 = $17.94<br />
                  المبلغ الصافي = $500 - $17.94 = <strong>$482.06</strong><br />
                  لكي يصل $500 كاملاً، يجب أن يُرسل العميل: <strong>$518.60</strong>
                </div>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  مثال 2: إرسال 200 يورو لصديق في ألمانيا
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الحالة:</strong> تريد إرسال 200 يورو كهدية لصديق في أوروبا.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>الحساب (تحويل دولي):</strong><br />
                  الرسوم = (200 × 4.5%) + €0.39 = €9.00 + €0.39 = €9.39<br />
                  يصل للصديق: €200 - €9.39 = <strong>€190.61</strong>
                </div>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  مثال 3: بائع متجر إلكتروني يحصل على 1000 ريال
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الحالة:</strong> بائع سعودي يتلقى دفعة 1000 ريال عبر PayPal.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>الحساب (معاملة تجارية قياسية):</strong><br />
                  الرسوم = (1000 × 3.49%) + 1.84 ر.س = 34.90 + 1.84 = <strong>36.74 ر.س</strong><br />
                  المبلغ الصافي = <strong>963.26 ر.س</strong>
                </div>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  مثال 4: دفع صغير بقيمة 5 دولارات
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الحالة:</strong> مقارنة رسوم المعاملات الصغيرة.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>معاملة عادية:</strong> (5 × 3.49%) + $0.49 = $0.174 + $0.49 = $0.664<br />
                  <strong>مدفوعات صغيرة:</strong> (5 × 5%) + $0.05 = $0.25 + $0.05 = $0.30<br />
                  <strong>الفرق:</strong> توفير $0.364 لكل معاملة — كبير بالنسبة لحجمها!
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>نصائح لتقليل رسوم PayPal</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  نصائح للبائعين والمستقلين:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>أضف الرسوم للسعر:</strong> إذا كان العميل يطلب الدفع عبر PayPal، أضف نسبة الرسوم لسعرك
                  </li>
                  <li>
                    <strong>استخدم Checkout للمتاجر:</strong> نسبة 2.99% أقل من المعاملات العادية 3.49%
                  </li>
                  <li>
                    <strong>جمّع المدفوعات:</strong> دفعة واحدة كبيرة أفضل من عدة دفعات صغيرة لأن الرسوم الثابتة تُدفع مرة واحدة
                  </li>
                  <li>
                    <strong>استكشف PayPal Business:</strong> قد يمنحك PayPal رسوماً مخفضة عند حجم معاملات عالٍ
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  أخطاء شائعة يجب تجنبها:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>لا تطلب من العميل أن يرسل المبلغ عبر "الأصدقاء" للتحايل على الرسوم — يخسر المشتري حماية المشتري</li>
                  <li>لا تنسَ رسوم تحويل العملة (قد تصل إلى 3-4% إضافية) عند التحويل الدولي</li>
                  <li>لا تحسب الرسوم من المبلغ المستلم فقط — الحاسبة تحسبها من المبلغ المُرسَل</li>
                  <li>تأكد من نوع المعاملة قبل إرسال أي مبلغ لتتجنب المفاجآت</li>
                </ul>
                <div
                  className="alert alert-warning mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>⚠️ تذكّر:</strong> رسوم PayPal تتغير من وقت لآخر. تأكد دائماً من الرسوم الرسمية على موقع PayPal قبل أي صفقة كبيرة.
                </div>
              </div>
            </div>
          </div>
          <br />
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>
            أسئلة متكررة
          </h2>
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>كم رسوم PayPal على 100 دولار؟</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  للمعاملة التجارية القياسية، رسوم 100 دولار هي: (100 × 3.49%) + $0.49 = $3.49 + $0.49 = <strong>$3.98</strong>. يصل للمستقبل $96.02. إذا أردت أن يصل $100 كاملاً، يجب إرسال حوالي $104.12.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>هل يمكن إرسال الأموال مجاناً عبر PayPal؟</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  نعم، داخل نفس الدولة يمكن إرسال المال مجاناً عبر خيار "الأصدقاء والعائلة" إذا كانت المصدر محفظة PayPal أو حساب بنكي مباشر. لكن إذا استخدمت بطاقة ائتمان، تُضاف رسوم حوالي 2.9%. التحويلات الدولية دائماً بها رسوم.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>ما الفرق بين المعاملة التجارية والشخصية في PayPal؟</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  المعاملة التجارية (Goods & Services) تُستخدم لشراء منتجات أو خدمات وتوفر حماية للمشتري والبائع، لكنها مرتبطة برسوم 3.49%. المعاملة الشخصية (Friends & Family) لإرسال الأموال للأشخاص القريبين ومجانية محلياً، لكنها لا توفر أي حماية في حالة النزاعات.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>هل PayPal يخصم رسوماً إضافية عند تحويل العملات؟</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  نعم، يُضيف PayPal هامشاً على سعر الصرف عادةً يتراوح بين 3% و4% فوق السعر الرسمي. هذه الرسوم منفصلة عن رسوم المعاملة. لتجنبها، يمكنك الإرسال بنفس عملة المستقبل إذا أمكن، أو استخدام خدمات مثل Wise للتحويلات الدولية برسوم أقل.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>كيف أحسب المبلغ الذي أطلبه ليصل مبلغ معين للعميل؟</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  استخدم خيار "أريد أن يصل مبلغ بعينه" في حاسبتنا. مثلاً، إذا كانت قيمة خدمتك 200 دولار وتريد الحصول عليها كاملة، أدخل 200 والحاسبة ستخبرك أن العميل يجب أن يُرسل $207.89 تقريباً للمعاملة التجارية القياسية.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>ما هي رسوم PayPal للمتاجر الإلكترونية؟</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  المتاجر الإلكترونية التي تستخدم PayPal Checkout تدفع 2.99% + رسوم ثابتة، وهي أقل من الرسوم القياسية (3.49%). بعض المتاجر ذات الحجم الكبير قد تحصل على أسعار مخفضة بعد التفاوض مع PayPal مباشرة.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>هل رسوم PayPal للريال السعودي والدرهم الإماراتي نفسها؟</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  النسبة المئوية متساوية (3.49% للمعاملات القياسية)، لكن الرسوم الثابتة تختلف: للريال السعودي 1.84 ر.س وللدرهم الإماراتي 1.80 د.إ. تستخدم حاسبتنا هذه القيم الفعلية لضمان دقة النتائج مع كل عملة.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>هل PayPal آمن للمعاملات التجارية الكبيرة؟</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  PayPal يوفر حماية جيدة للمشترين والبائعين في المعاملات التجارية، لكن للمبالغ الكبيرة جداً قد تكون هناك حدود على التحويل تحتاج إلى التحقق منها. كذلك قد يجمّد PayPal الحسابات الجديدة ذات المعاملات الكبيرة المفاجئة. دائماً راجع سياسات PayPal الخاصة ببلدك قبل التعامل بمبالغ كبيرة.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaypalCalculator;